(() => {
  if (window.humblerGiftGuideReady) return;
  window.humblerGiftGuideReady = true;
  const cleanups = new Map();
  const SELECTOR = "[data-fathers-day-guide]";
  const REVEAL_SELECTOR = ".fathers-day-warranty[data-fdg-reveal]";
  const WARRANTY_MOTION_SELECTOR = ".fathers-day-warranty--product-visual";
  const WARRANTY_CARD_SELECTOR = "[data-fdg-visual-card]";
  const TRIGGER_SELECTOR = "[data-fdg-builder-open]";

  const prefersReducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const show = (element) => {
    element.classList.add("is-visible");
  };

  const setupReveals = (root, cleanup) => {
    const items = Array.from(root.querySelectorAll(REVEAL_SELECTOR)).filter(
      (item) => item.dataset.fdgObserved !== "true"
    );
    if (items.length === 0) return;

    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      items.forEach(show);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          show(entry.target);
          observer.unobserve(entry.target);
        });
      },
      {
        rootMargin: "0px 0px -12% 0px",
        threshold: 0.16
      }
    );

    cleanup.push(() => observer.disconnect());
    items.forEach((item, index) => {
      item.dataset.fdgObserved = "true";
      item.style.transitionDelay = `${Math.min(index * 70, 280)}ms`;
      observer.observe(item);
    });
  };

  const openBuilder = () => {
    const opener = document.querySelector("[data-buy-any-two-open]");
    if (!opener) return false;

    opener.click();
    return true;
  };

  const setupBuilderTriggers = (root, cleanup) => {
    const events = new AbortController();
    cleanup.push(() => events.abort());
    root.addEventListener("click", (event) => {
      const trigger = event.target.closest(TRIGGER_SELECTOR);
      if (!trigger) return;

      event.preventDefault();
      openBuilder();
    }, { signal: events.signal });
  };

  const setupWarrantyMotion = (root, cleanup) => {
    const panels = Array.from(
      root.matches?.(WARRANTY_MOTION_SELECTOR)
        ? [root]
        : root.querySelectorAll(WARRANTY_MOTION_SELECTOR)
    ).filter((panel) => panel.dataset.fdgMotionReady !== "true");

    if (panels.length === 0) return;

    if (prefersReducedMotion()) {
      panels.forEach((panel) => {
        panel.dataset.fdgMotionReady = "true";
      });
      return;
    }

    const start = () => {
      if (!window.gsap) return false;

      const animator = window.gsap;

      panels.forEach((panel) => {
        if (panel.dataset.fdgMotionReady === "true") return;

        const visual = panel.querySelector(".fathers-day-warranty__visual");
        const viewport = panel.querySelector(".fathers-day-warranty__visual-viewport");
        const track = panel.querySelector(".fathers-day-warranty__visual-track");
        const cards = Array.from(panel.querySelectorAll(WARRANTY_CARD_SELECTOR)).filter(
          (card) => card.dataset.fdgClone !== "true"
        );
        const badge = panel.querySelector(".fathers-day-warranty__visual-copy");

        if (!visual || !viewport || !track || cards.length < 2) {
          panel.dataset.fdgMotionReady = "true";
          return;
        }

        panel.dataset.fdgMotionReady = "true";
        panel.fdgMotionVisible = typeof IntersectionObserver === "undefined";
        const setMotionVisible = (visible) => {
          panel.fdgMotionVisible = visible && !document.hidden;
          [panel.fdgMotionLoop, panel.fdgBadgeIntro, panel.fdgBadgeLoop].forEach((motion) => {
            if (panel.fdgMotionVisible) motion?.resume();
            else motion?.pause();
          });
        };
        if (typeof IntersectionObserver !== "undefined") {
          const visibilityObserver = new IntersectionObserver((entries) => {
            panel.fdgIntersects = entries[0]?.isIntersecting;
            setMotionVisible(panel.fdgIntersects);
          }, { threshold: 0.01 });
          visibilityObserver.observe(panel);
          cleanup.push(() => visibilityObserver.disconnect());
        }
        const visibilityEvents = new AbortController();
        document.addEventListener("visibilitychange", () => setMotionVisible(panel.fdgIntersects ?? true), { signal: visibilityEvents.signal });
        cleanup.push(() => visibilityEvents.abort());

        const clones = cards.map((card) => {
          const clone = card.cloneNode(true);
          clone.dataset.fdgClone = "true";
          clone.setAttribute("aria-hidden", "true");
          track.appendChild(clone);
          return clone;
        });

        const allCards = [...cards, ...clones];
        let lastViewportWidth = 0;

        const buildLoop = () => {
          const viewportWidth = Math.round(viewport.clientWidth);
          if (!viewportWidth || viewportWidth === lastViewportWidth) return;

          lastViewportWidth = viewportWidth;
          panel.fdgMotionLoop?.kill();

          const firstCardWidth = cards[0].offsetWidth;
          const cycleWidth = Math.max(1, clones[0].offsetLeft - cards[0].offsetLeft);
          const startX = Math.max(0, (viewportWidth - firstCardWidth) / 2);
          const duration = Math.max(24, cycleWidth / 28);

          animator.set(track, { x: startX, y: 0, yPercent: -50 });
          panel.fdgMotionLoop = animator.to(track, {
            x: startX - cycleWidth,
            duration,
            ease: "none",
            repeat: -1,
            paused: !panel.fdgMotionVisible
          });
        };

        animator.set(allCards, { autoAlpha: 1, clearProps: "filter,zIndex" });
        animator.set(track, { clearProps: "transform" });
        animator.set(track, { x: 0, y: 0, yPercent: -50 });

        const frame = window.requestAnimationFrame(() => {
          buildLoop();

          if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(buildLoop);
            observer.observe(viewport);
            panel.fdgMotionObserver = observer;
          }
        });

        if (badge) {
          panel.fdgBadgeIntro = animator.fromTo(
            badge,
            { autoAlpha: 0, xPercent: -50, y: 14 },
            { autoAlpha: 1, xPercent: -50, y: 0, duration: 0.62, ease: "power2.out", delay: 0.18, paused: !panel.fdgMotionVisible }
          );

          panel.fdgBadgeLoop = animator.to(badge, {
            y: -2,
            duration: 1.8,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut",
            delay: 1.15,
            paused: !panel.fdgMotionVisible
          });
        }
        cleanup.push(() => {
          window.cancelAnimationFrame(frame);
          panel.fdgMotionObserver?.disconnect();
          panel.fdgMotionLoop?.kill();
          panel.fdgBadgeIntro?.kill();
          panel.fdgBadgeLoop?.kill();
          clones.forEach((clone) => clone.remove());
          delete panel.dataset.fdgMotionReady;
        });
      });

      return true;
    };

    if (start()) return;

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (start() || attempts > 30) {
        window.clearInterval(timer);
      }
    }, 80);
    cleanup.push(() => window.clearInterval(timer));
  };

  const init = (root) => {
    if (!root || root.dataset.fdgReady === "true") return;
    root.dataset.fdgReady = "true";

    const cleanup = [];
    cleanups.set(root, cleanup);
    setupReveals(root, cleanup);
    setupBuilderTriggers(root, cleanup);
    setupWarrantyMotion(root, cleanup);
  };

  const initAll = () => {
    document.querySelectorAll(SELECTOR).forEach(init);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:unload", (event) => {
    cleanups.forEach((callbacks, root) => {
      if (!event.target.contains(root)) return;
      callbacks.forEach((cleanup) => cleanup());
      cleanups.delete(root);
      delete root.dataset.fdgReady;
    });
  });

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target.matches?.(SELECTOR)) {
      init(event.target);
      return;
    }

    event.target.querySelectorAll?.(SELECTOR).forEach(init);
  });
})();
